import { useCallback, useState } from 'react';
import {
  currentStatus,
  isPushConfigured,
  requestPermission,
  subscribeToPush,
  unsubscribeFromPush,
} from '../../infrastructure/notifications/pushClient';
import {
  saveSubscription,
  deleteSubscription,
} from '../../infrastructure/repositories/pushSubscriptionRepository';
import { shouldShowPrompt, type NotificationStatus } from '../../domain/notifications/policy';

const DISMISSED_KEY = 'swol_notifications_dismissed_at';
/**
 * Whether the switch is on.
 *
 * Needed because permission is one-way: the browser will grant it, but nothing
 * in a web app can hand it back. So "off" is our own state - we drop the push
 * subscription and remember the choice - and turning it on again costs no second
 * permission prompt.
 */
const ENABLED_KEY = 'swol_notifications_enabled';

function readDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeDismissedAt(at: number): void {
  try {
    localStorage.setItem(DISMISSED_KEY, String(at));
  } catch {
    // Private mode. Worst case the prompt reappears next launch.
  }
}

/** Granted-but-switched-off is the only state we have to remember ourselves. */
function readEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, String(enabled));
  } catch {
    // Private mode; the switch simply resets next launch.
  }
}

export interface NotificationsState {
  status: NotificationStatus;
  /** True once a VAPID key exists, so subscriptions can actually be taken. */
  isConfigured: boolean;
  /** Whether the switch reads as on: permission granted and not switched off. */
  isOn: boolean;
  /** False where the switch could not do anything - blocked, or no support. */
  canToggle: boolean;
  isBusy: boolean;
  /** Whether the prompt should be on screen right now. */
  shouldPrompt: boolean;
  /** Flips the switch, asking for permission the first time. */
  toggle: () => Promise<void>;
  enable: () => Promise<NotificationStatus>;
  dismissPrompt: () => void;
}

/**
 * The notification state for the signed-in person.
 *
 * Status is read from the browser rather than stored, because the browser is the
 * only thing that knows - permission can be changed in OS settings while the app
 * is closed, and any copy we kept would quietly rot.
 */
export function useNotifications(userId: string | undefined): NotificationsState {
  const [status, setStatus] = useState<NotificationStatus>(() => currentStatus());
  const [isBusy, setIsBusy] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(() => readDismissedAt());
  const [enabled, setEnabled] = useState<boolean>(() => readEnabled());
  // Read once at mount rather than on every render: reading the clock while
  // rendering is impure, and against a seven-day window the exact instant is
  // beside the point. A dismissal stamps a later time than this, so the prompt
  // still disappears immediately when it is dismissed.
  const [openedAt] = useState(() => Date.now());

  const enable = useCallback(async () => {
    setIsBusy(true);
    try {
      const next = await requestPermission();
      setStatus(next);

      if (next === 'granted') {
        writeEnabled(true);
        setEnabled(true);
      }

      if (next === 'granted' && userId) {
        // Absent a VAPID key this returns null, which is expected rather than a
        // failure: permission is the part that needed the person.
        const subscription = await subscribeToPush();
        if (subscription) {
          try {
            await saveSubscription(userId, subscription);
          } catch (error) {
            console.error('Could not save the push subscription:', error);
          }
        }
      }
      return next;
    } finally {
      setIsBusy(false);
    }
  }, [userId]);

  const disable = useCallback(async () => {
    setIsBusy(true);
    try {
      writeEnabled(false);
      setEnabled(false);
      const endpoint = await unsubscribeFromPush();
      if (endpoint) {
        try {
          await deleteSubscription(endpoint);
        } catch (error) {
          console.error('Could not remove the push subscription:', error);
        }
      }
      // Permission stays granted - only the browser can take that back - so the
      // switch, not the permission, is what "off" means here.
      setStatus(currentStatus());
    } finally {
      setIsBusy(false);
    }
  }, []);

  const dismissPrompt = useCallback(() => {
    const now = Date.now();
    writeDismissedAt(now);
    setDismissedAt(now);
  }, []);

  const isOn = status === 'granted' && enabled;
  const canToggle = status === 'granted' || status === 'default';

  const toggle = useCallback(async () => {
    if (isOn) {
      await disable();
      return;
    }
    // Already granted and merely switched off: no second permission prompt.
    await enable();
  }, [isOn, enable, disable]);

  return {
    status,
    isConfigured: isPushConfigured(),
    isOn,
    canToggle,
    isBusy,
    shouldPrompt: shouldShowPrompt(status, dismissedAt, openedAt),
    toggle,
    enable,
    dismissPrompt,
  };
}
